import { IVectorLayerGroupConfig } from "@esdm/map/dist/layers/groups/vector/interfaces/IVectorLayerGroupConfig";
import { WfsParamSourceMapServer, IWfsParamSource, WfsBboxAsWktRequestParams } from "@esdm/map/dist/layers/vector/sources/wfs";
import { VectorLayerStrategy } from "@esdm/map/dist/layers/vector/VectorLayerStrategy";
import { CheshireRecordType, CheshireRecordSubType } from "./CheshireRecordTypes";
import { IWmsLayerGroupConfig } from "@esdm/map/dist/layers/groups/wms/interfaces/IWmsLayerGroupConfig";

export class DefaultLayerConfigurationFactory {

    private mapFileUrl: string = "/api/OgcService/hbsmr";

    private paramSource(): IWfsParamSource {
        return new WfsParamSourceMapServer(new WfsBboxAsWktRequestParams(),{srsName: "EPSG:27700"});
    }

    constructor() {
    }

    public layerConfig = (
        recordType: CheshireRecordType,
        recordSubType: CheshireRecordSubType = null,
        opts: IVectorLayerGroupConfig = {}
    ): IVectorLayerGroupConfig | IWmsLayerGroupConfig => {
        let layerConfig: IVectorLayerGroupConfig | IWmsLayerGroupConfig = null;

        switch (recordType) {
            case CheshireRecordType.Monument:
                layerConfig = this.Monument(opts);
                break;         

            case CheshireRecordType.Event:
                layerConfig = this.Event(opts);
                break;   

        }

        if (layerConfig === null && recordSubType !== null) {
            switch (recordSubType) {

                case CheshireRecordSubType.ListedBuilding:
                    layerConfig = this.ListedBuilding(opts);
                    break;

                case CheshireRecordSubType.LocallyListedBuilding:
                    layerConfig = this.LocallyListedBuilding(opts);
                    break;

                case CheshireRecordSubType.ScheduledMonument:
                    layerConfig = this.ScheduledMonument(opts);
                    break;    
                                
                case CheshireRecordSubType.RegisteredParksAndGardens:
                    layerConfig = this.RegisteredParksAndGardens(opts);
                    break; 

                case CheshireRecordSubType.RegisteredBattlefields:
                    layerConfig = this.RegisteredBattlefields(opts);
                    break; 

                case CheshireRecordSubType.ConservationArea:
                    layerConfig = this.ConservationArea(opts);
                    break; 

                case CheshireRecordSubType.AreaArchaeologicalImportance:
                    layerConfig = this.AreaArchaeologicalImportance(opts);
                    break;

                case CheshireRecordSubType.AreaArchaeologicalPotential:
                    layerConfig = this.AreaArchaeologicalPotential(opts);
                    break;

                case CheshireRecordSubType.AreaSpecialArchaeologicalPotential:
                    layerConfig = this.AreaSpecialArchaeologicalPotential(opts);
                    break;

                case CheshireRecordSubType.SHINE:
                    layerConfig = this.SHINE(opts);
                    break;

                case CheshireRecordSubType.WorldHeritageSite:
                    layerConfig = this.WorldHeritageSite(opts);
                    break;

                case CheshireRecordSubType.UADCharacterZones:
                    layerConfig = this.UADCharacterZones(opts);
                    break;
            }
        }

        return layerConfig;
    }

    private Monument = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Monuments",
                ref: "Monuments",
                type: CheshireRecordType.Monument,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                wfsParams: this.paramSource(),
                maxFeatureCount: 5000,
                //legend: { point: false, line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "MonumentPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "MonumentLinesWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "MonumentPointsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }                    
                ]
            }, ...opts
        };
    }

    private Event = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Events",
                ref: "Events",
                type: CheshireRecordType.Event,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                wfsParams: this.paramSource(),
                maxFeatureCount: 5000,    
                legend: { line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "EventPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "EventLinesWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "EventPointsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    private ListedBuilding = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Listed Buildings",
                ref: "ListedBuildings",
                type: CheshireRecordType.Designation,
                subType: CheshireRecordSubType.ListedBuilding,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: CheshireRecordSubType.ListedBuilding },
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                legend: { line: false},
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPointsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    private LocallyListedBuilding = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Locally Listed Buildings",
                ref: "LocallyListedBuildings",
                type: CheshireRecordType.Designation,
                subType: CheshireRecordSubType.LocallyListedBuilding,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: CheshireRecordSubType.LocallyListedBuilding },
                wfsParams: this.paramSource(),
                maxFeatureCount: 10000,
                legend: { line: false},
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPointsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    },
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    private ScheduledMonument = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Scheduled Monuments",
                ref: "ScheduledMonuments",
                type: CheshireRecordType.Designation,
                subType: CheshireRecordSubType.ScheduledMonument,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: CheshireRecordSubType.ScheduledMonument },
                wfsParams: this.paramSource(),
                maxFeatureCount: 5000,
                legend: { line: false},
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }
 
    private RegisteredParksAndGardens = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Registered Parks and Gardens",
                ref: "RegisteredParksAndGardens",
                type: CheshireRecordType.Designation,
                subType: CheshireRecordSubType.RegisteredParksAndGardens,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: CheshireRecordSubType.RegisteredParksAndGardens },
                wfsParams: this.paramSource(),
                maxFeatureCount: 5000,
                legend: { line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    private AreaArchaeologicalImportance = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Area of Archaeological Importance",
                ref: "AreaArchaeologicalImportance",
                type: CheshireRecordType.Designation,
                subType: CheshireRecordSubType.AreaArchaeologicalImportance,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: CheshireRecordSubType.AreaArchaeologicalImportance },
                wfsParams: this.paramSource(),
                maxFeatureCount: 5000,
                legend: { line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    private AreaArchaeologicalPotential = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Areas of Archaeological Potential",
                ref: "AreaArchaeologicalPotential",
                type: CheshireRecordType.Designation,
                subType: CheshireRecordSubType.AreaArchaeologicalPotential,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: CheshireRecordSubType.AreaArchaeologicalPotential },
                wfsParams: this.paramSource(),
                maxFeatureCount: 5000,
                legend: { line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    private AreaSpecialArchaeologicalPotential = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Area of Special<br/> Archaeological Potential",
                ref: "AreaSpecialArchaeologicalPotential",
                type: CheshireRecordType.Designation,
                subType: CheshireRecordSubType.AreaSpecialArchaeologicalPotential,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: CheshireRecordSubType.AreaSpecialArchaeologicalPotential },
                wfsParams: this.paramSource(),
                maxFeatureCount: 5000,
                legend: { line: false},
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }
    
    private ConservationArea = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Conservation Areas",
                ref: "ConservationArea",
                type: CheshireRecordType.Designation,
                subType: CheshireRecordSubType.ConservationArea,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: CheshireRecordSubType.ConservationArea },
                wfsParams: this.paramSource(),
                maxFeatureCount: 5000,
                legend: { line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }
    
    private RegisteredBattlefields = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Registered Battlefields",
                ref: "RegisteredBattlefields",
                type: CheshireRecordType.Designation,
                subType: CheshireRecordSubType.RegisteredBattlefields,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: CheshireRecordSubType.RegisteredBattlefields },
                wfsParams: this.paramSource(),
                maxFeatureCount: 5000,
                legend: { line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    private SHINE = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Selected Heritage Inventory<br/> for Natural England",
                ref: "SHINE",
                type: CheshireRecordType.Designation,
                subType: CheshireRecordSubType.SHINE,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: CheshireRecordSubType.SHINE },
                wfsParams: this.paramSource(),
                maxFeatureCount: 5000,
                legend: { line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    private WorldHeritageSite = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "World Heritage Site",
                ref: "WorldHeritageSite",
                type: CheshireRecordType.Designation,
                subType: CheshireRecordSubType.WorldHeritageSite,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: CheshireRecordSubType.WorldHeritageSite },
                wfsParams: this.paramSource(),
                maxFeatureCount: 5000,
                legend: { line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "DesignationPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    private UADCharacterZones = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Archaeological Character Zones",
                ref: "ArchaeologicalCharacterZones",
                type: CheshireRecordType.HLC,
                subType: CheshireRecordSubType.UADCharacterZones,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                urlParams: { recsubtypecode: CheshireRecordSubType.UADCharacterZones },
                wfsParams: this.paramSource(),
                maxFeatureCount: 5000,
                legend: { line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "HLCPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }


}

